'use client'

/**
 * HyperDX Browser SDK Client Component
 *
 * This component initializes HyperDX browser monitoring for:
 * - Session replay
 * - Console logs capture
 * - Network requests (XHR/Fetch/WebSocket)
 * - Frontend exceptions
 * - User actions tracking
 *
 * Documentation: https://www.hyperdx.io/docs/install/browser
 */

import { useEffect } from 'react'
import HyperDX from '@hyperdx/browser'

export function HyperDXClient() {
  useEffect(() => {
    // Only initialize if API key is provided
    const apiKey = process.env.NEXT_PUBLIC_HYPERDX_API_KEY

    if (!apiKey) {
      console.warn('[HyperDX] Browser SDK not initialized: NEXT_PUBLIC_HYPERDX_API_KEY is missing')
      return
    }

    try {
      HyperDX.init({
        // Your HyperDX Ingestion API Key
        apiKey: apiKey,

        // Service name for frontend events
        service: process.env.NEXT_PUBLIC_HYPERDX_SERVICE_NAME || 'agritech-frontend',

        // OpenTelemetry collector URL (required for self-hosted instances)
        url: process.env.NEXT_PUBLIC_HYPERDX_OTEL_URL || undefined,

        // Regex patterns to link frontend and backend traces
        // Adds 'traceparent' header to matching requests for distributed tracing
        // All backends have CORS configured to accept traceparent/tracestate/baggage
        tracePropagationTargets: [
          /api\.agricompany-dev\.com\.br/i,
          /api\.agricompany\.tech/i
        ],

        // Capture console logs
        consoleCapture: true,

        // Capture XHR/Fetch/WebSocket network requests
        advancedNetworkCapture: true,

        // Privacy settings for session replay
        // NOTE: maskAllInputs cannot be configured per-page, only globally
        maskAllInputs: false,
        maskAllText: false,

        // Disable Intercom integration if you don't use it
        disableIntercom: true,
      })

      console.log('[HyperDX] Browser SDK initialized successfully')
    } catch (error) {
      console.error('[HyperDX] Failed to initialize browser SDK:', error)
    }
  }, [])

  // This component doesn't render anything
  return null
}

/**
 * Helper functions to use throughout your application
 */

/**
 * Attach user information to HyperDX session
 * Call this after user login to associate sessions with user data
 *
 * @example
 * ```ts
 * attachUserInfo({
 *   userId: user.id,
 *   userEmail: user.email,
 *   userName: user.name,
 *   teamName: user.team,
 * })
 * ```
 */
export function attachUserInfo(info: {
  userId?: string | number
  userEmail?: string
  userName?: string
  teamName?: string
  [key: string]: any
}) {
  try {
    HyperDX.setGlobalAttributes(info)
  } catch (error) {
    console.error('[HyperDX] Failed to attach user info:', error)
  }
}

/**
 * Track custom user actions/events
 *
 * @example
 * ```ts
 * trackAction('Form-Submitted', {
 *   formId: 'signup-form',
 *   formName: 'Signup Form',
 * })
 * ```
 */
export function trackAction(actionName: string, metadata?: Record<string, any>) {
  try {
    HyperDX.addAction(actionName, metadata)
  } catch (error) {
    console.error('[HyperDX] Failed to track action:', error)
  }
}

/**
 * Enable advanced network capture dynamically
 */
export function enableNetworkCapture() {
  try {
    HyperDX.enableAdvancedNetworkCapture()
  } catch (error) {
    console.error('[HyperDX] Failed to enable network capture:', error)
  }
}

/**
 * Disable advanced network capture dynamically
 */
export function disableNetworkCapture() {
  try {
    HyperDX.disableAdvancedNetworkCapture()
  } catch (error) {
    console.error('[HyperDX] Failed to disable network capture:', error)
  }
}
